package com.evoapps.evoshift.util

import androidx.compose.ui.text.intl.Locale
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/** The languages the app is translated into (values/ is English, values-pl Polish, values-uk Ukrainian). */
val APP_LANGUAGES = listOf("en", "pl", "uk")

/**
 * The language picked in the app (SH-137), above the device's: e.g. a Ukrainian worker whose
 * phone is set to Polish. Null: the device's language. Changed only through
 * [com.evoapps.evoshift.settings.AppLanguageSettings], which also remembers it; this object is
 * what the rest of the app (and [appLanguage]) reads, also outside composition.
 */
object AppLocale {
    private val state = MutableStateFlow<String?>(null)

    /** The picked language, or null for the device's. */
    val chosen: StateFlow<String?> = state.asStateFlow()

    /**
     * Picks [language] (null: the device's) and points the platform's locale at it at once, so
     * Compose resources, dates and numbers follow (see [applyPlatformLanguage]).
     */
    fun set(language: String?) {
        val value = language?.takeIf { it in APP_LANGUAGES }
        applyPlatformLanguage(value)
        state.value = value
    }
}

/**
 * The language the app shows: the one picked in the app (SH-137), else Polish on Polish devices,
 * Ukrainian on Ukrainian ones (SH-119), English otherwise. E-mails and push notifications the
 * server sends on the person's behalf use the same (SH-138: it is saved on the profile).
 */
fun appLanguage(): String = AppLocale.chosen.value ?: supportedLanguage(Locale.current.language)

/** [language] if the app is translated into it, English otherwise. */
fun supportedLanguage(language: String): String = if (language in APP_LANGUAGES) language else "en"

/**
 * Whether numbers in [language] use a decimal comma ("7,5", Polish and Ukrainian) rather than a
 * point ("7.5", English). CSV exports in those languages also use ";" between columns, as Excel
 * set to Polish or Ukrainian expects.
 */
fun usesDecimalComma(language: String): Boolean = language == "pl" || language == "uk"
